const PREFIX = "blogic.formDraft.";
const VERSION = 1;
const HOUR_MS = 60 * 60 * 1000;

export const FORM_DRAFT_TTL_MS = {
  sensitive: 24 * HOUR_MS,
  standard: 72 * HOUR_MS,
};

export interface FormDraft<T> {
  version: number;
  lastEditAt: number;
  step: number;
  maxValidated: number;
  sensitive: boolean;
  files: Record<string, string>;
  values: Partial<T>;
}

export type FormDraftInput<T> = Omit<FormDraft<T>, "version">;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function expiresAt(draft: FormDraft<unknown>): number {
  return draft.lastEditAt + (draft.sensitive ? FORM_DRAFT_TTL_MS.sensitive : FORM_DRAFT_TTL_MS.standard);
}

function parse(raw: string): FormDraft<unknown> | null {
  try {
    const d = JSON.parse(raw) as FormDraft<unknown> | null;
    if (!d || typeof d !== "object") return null;
    if (d.version !== VERSION || typeof d.lastEditAt !== "number") return null;
    if (!d.values || typeof d.values !== "object") return null;
    return {
      ...d,
      step: Number.isInteger(d.step) ? d.step : 0,
      maxValidated: Number.isInteger(d.maxValidated) ? d.maxValidated : 0,
      sensitive: d.sensitive === true,
      files: d.files && typeof d.files === "object" ? d.files : {},
    };
  } catch {
    return null;
  }
}

export function formDraftKey(...parts: (string | null | undefined)[]): string {
  return PREFIX + parts.map((p) => encodeURIComponent(p ?? "")).join(":");
}

export function readFormDraft<T>(key: string, now = Date.now()): FormDraft<T> | null {
  const s = storage();
  if (!s) return null;
  let raw: string | null;
  try {
    raw = s.getItem(key);
  } catch {
    return null;
  }
  if (raw == null) return null;
  const draft = parse(raw);
  if (!draft || expiresAt(draft) <= now) {
    removeFormDraft(key);
    return null;
  }
  return draft as FormDraft<T>;
}

export function writeFormDraft<T>(key: string, draft: FormDraftInput<T>): boolean {
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(key, JSON.stringify({ ...draft, version: VERSION }));
    return true;
  } catch {
    return false;
  }
}

export function removeFormDraft(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    return;
  }
}

export function sweepExpiredFormDrafts(now = Date.now()): void {
  const s = storage();
  if (!s) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      const raw = s.getItem(k);
      const draft = raw == null ? null : parse(raw);
      if (!draft || expiresAt(draft) <= now) s.removeItem(k);
    }
  } catch {
    return;
  }
}
